import type { ComponentProps } from 'react';
import {
  Controller,
  type Control,
  type UseFormRegister,
} from 'react-hook-form';

import { Badge } from '@repo/ui/Badge';
import { BadgeCopy } from '@repo/ui/Badge/BadgeCopy';
import { Flex } from '@repo/ui/Flex';
import { InputField } from '@repo/ui/Input/InputField';
import { InputGroup } from '@repo/ui/Input/InputGroup';
import { InputLabel } from '@repo/ui/Input/InputLabel';
import { InputRoot } from '@repo/ui/Input/InputRoot';
import { TextareaControl } from '@repo/ui/Input/TextareaControl';
import { TextareaCounter } from '@repo/ui/Input/TextareaCounter';
import { VisuallyHidden } from '@repo/ui/VisuallyHidden';

import { Dropdown } from '../../Dropdown/Dropdown';

import { rawString, Text } from '../../Language/Language';
import { ShipmentItemsPreview } from './ShipmentItemsPreview';
import { PhotoUpload } from './PhotoUpload';
import { AttestationCheckbox } from './AttestationCheckbox';
import { TaskHeader } from '../TaskPreview/TaskPreview';
import { COMMENT_MAX_LENGTH, type Photo } from './ReasonsTaskSchema';
import type { WorkflowData } from '../../../workflow';
import type { Order } from '../../../graphql/query/getOrder';

import s from './ReasonsTask.module.css';

export interface Reason {
  code: string;
  title: string;
  retailerReasonCode?: string;
  retailerReasonCodeId?: string;
  resolutions?: unknown;
  fields?: unknown;
  parentId?: string | null;
}

export interface ReasonsTaskViewProps {
  itemId?: string;
  reasons: Reason[];
  nestedReasons?: Reason[];
  isNestedReasonRequired: boolean;
  selectedRootReason?: Reason;
  shouldShowPreview: boolean;
  order?: Order | null;
  photos?: Photo[];
  attestationChecked?: boolean;
  reasonId?: string;
  commentConfig?: {
    enabled: boolean;
    required: boolean;
    metadataId?: string;
  };
  photoConfig?: {
    enabled: boolean;
    required: boolean;
    max?: number;
    id?: string;
  };
  attestationConfig?: {
    enabled: boolean;
    required: boolean;
    metadataId?: string;
  };
  // Callbacks
  onReasonChange?: ComponentProps<'select'>['onChange'];
  onPhotoUpload: (photos: Photo[]) => void;
  onPhotoRemove: (photo: Photo) => void;
  onPhotoReset: () => void;
  onAttestationChange: (checked: boolean) => void;
  // Form integration
  control?: Control<WorkflowData>;
  register?: UseFormRegister<WorkflowData>;
}

/**
 * Pure presentational component for displaying reason selection.
 * Contains no hooks or business logic.
 * All data and callbacks provided via props.
 */
export const ReasonsTaskView = ({
  itemId,
  reasons,
  nestedReasons,
  isNestedReasonRequired,
  selectedRootReason,
  shouldShowPreview,
  order,
  photos,
  attestationChecked,
  reasonId,
  commentConfig,
  photoConfig,
  attestationConfig,
  onReasonChange,
  onPhotoUpload,
  onPhotoRemove,
  onPhotoReset,
  onAttestationChange,
  control,
  register,
}: ReasonsTaskViewProps) => {
  if (!reasons || reasons.length < 1) return null;

  return (
    <>
      <TaskHeader
        id={`${itemId}-reasonsTitle`}
        language={{ langKey: 'Global.reasons.reasonSelection.Header' }}
      />

      <Flex
        direction="row"
        gap="g-16"
        wrap="wrap"
        padding={['md:p-24', 'p-16']}
      >
        <div className={s.reasonContainer}>
          {control && itemId ? (
            <Controller
              name={`items.${itemId}.reason.reasonId`}
              control={control}
              render={({ field }) => (
                <Dropdown
                  className={
                    shouldShowPreview
                      ? s.selectWrapperWithPreview
                      : s.selectWrapper
                  }
                  id={`${itemId}-reason-select`}
                  aria-labelledby={`${itemId}-reasonsTitle`}
                  data-testid="reasons"
                  label={rawString({
                    langKey: 'Global.reasons.reasonSelection.label',
                  })}
                  labelVariant="embedded"
                  required
                  placeholder={rawString({
                    langKey: 'Global.reasons.reasonSelection.placeholder',
                  })}
                  options={reasons.map((reason) => ({
                    value: reason.code,
                    label:
                      rawString({
                        langKey: `Global.reason.${reason.code}.title`,
                        fallback: reason.title,
                      }) ?? reason.title,
                  }))}
                  value={field.value}
                  onValueChange={(value) => {
                    onReasonChange?.({
                      target: { name: field.name, value },
                    } as unknown as React.ChangeEvent<HTMLSelectElement>);

                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                />
              )}
            />
          ) : (
            <Dropdown
              className={
                shouldShowPreview ? s.selectWrapperWithPreview : s.selectWrapper
              }
              id={`${itemId}-reason-select`}
              aria-labelledby={`${itemId}-reasonsTitle`}
              data-testid="reasons"
              label={rawString({
                langKey: 'Global.reasons.reasonSelection.label',
              })}
              labelVariant="embedded"
              required
              placeholder={rawString({
                langKey: 'Global.reasons.reasonSelection.placeholder',
              })}
              options={reasons.map((reason) => ({
                value: reason.code,
                label:
                  rawString({
                    langKey: `Global.reason.${reason.code}.title`,
                    fallback: reason.title,
                  }) ?? reason.title,
              }))}
            />
          )}
          {shouldShowPreview && selectedRootReason && order && (
            <ShipmentItemsPreview
              selectedReason={
                selectedRootReason as unknown as Parameters<
                  typeof ShipmentItemsPreview
                >[0]['selectedReason']
              }
              order={order}
              currentItemId={itemId ?? ''}
              shipmentIndex={undefined}
            />
          )}
        </div>
        {isNestedReasonRequired && (
          <Flex direction="column" gap="g-16">
            <VisuallyHidden id={`${itemId}-nestedReasonsTitle`}>
              <Text langKey="Global.reasons.nestedReasonSelection.label" />
            </VisuallyHidden>
            {control && itemId ? (
              <Controller
                name={`items.${itemId}.reason.nestedReasonId`}
                control={control}
                render={({ field }) => (
                  <Dropdown
                    className={s.selectWrapper}
                    id={`${itemId}-nested-reason-select`}
                    aria-labelledby={`${itemId}-nestedReasonsTitle`}
                    data-testid="nestedReasons"
                    label={rawString({
                      langKey: 'Global.reasons.nestedReasonSelection.label',
                    })}
                    labelVariant="embedded"
                    required
                    placeholder={rawString({
                      langKey:
                        'Global.reasons.nestedReasonSelection.placeholder',
                    })}
                    options={
                      nestedReasons?.map((reason) => ({
                        value: reason.code,
                        label:
                          rawString({
                            langKey: `Global.reason.${reason.code}.title`,
                            fallback: reason.title,
                          }) ?? reason.title,
                      })) ?? []
                    }
                    value={field.value}
                    onValueChange={(value) => {
                      // CRITICAL: Call shipment scope logic BEFORE updating field
                      // This ensures virtual items are added before form state updates
                      onReasonChange?.({
                        target: { name: field.name, value },
                      } as unknown as React.ChangeEvent<HTMLSelectElement>);

                      // Now update the field value
                      field.onChange(value);
                    }}
                    onBlur={field.onBlur}
                    name={field.name}
                  />
                )}
              />
            ) : (
              <Dropdown
                className={s.selectWrapper}
                id={`${itemId}-nested-reason-select`}
                aria-labelledby={`${itemId}-nestedReasonsTitle`}
                data-testid="nestedReasons"
                label={rawString({
                  langKey: 'Global.reasons.nestedReasonSelection.label',
                })}
                labelVariant="embedded"
                required
                placeholder={rawString({
                  langKey: 'Global.reasons.nestedReasonSelection.placeholder',
                })}
                options={
                  nestedReasons?.map((reason) => ({
                    value: reason.code,
                    label:
                      rawString({
                        langKey: `Global.reason.${reason.code}.title`,
                        fallback: reason.title,
                      }) ?? reason.title,
                  })) ?? []
                }
              />
            )}
          </Flex>
        )}
        {commentConfig?.enabled && (
          <Flex direction="column" gap="g-16">
            <label
              htmlFor={`items.${itemId}.reason.comments.value`}
              className={s.fieldTitle}
            >
              <Text langKey="Global.reasons.comments.Header" />
            </label>
            <InputRoot width="full">
              <InputGroup>
                <InputField>
                  <InputLabel
                    htmlFor={`items.${itemId}.reason.comments.value`}
                    isFloating
                  >
                    <Text langKey="Global.reasons.comments.Label" />
                  </InputLabel>
                  <TextareaControl
                    id={`items.${itemId}.reason.comments.value`}
                    maxLength={COMMENT_MAX_LENGTH}
                    required={commentConfig.required}
                    {...(register && itemId
                      ? register(`items.${itemId}.reason.comments.value`)
                      : {
                          name: `items.${itemId}.reason.comments.value` as const,
                        })}
                  />
                </InputField>
                <Flex justifyContent="space-between" alignItems="center">
                  <TextareaCounter />
                  <Badge size="sm" type="neutral" className={s.optionalBadge}>
                    <BadgeCopy>
                      {commentConfig.required
                        ? rawString({
                            langKey: 'Global.textarea.required',
                          })
                        : rawString({
                            langKey: 'Global.textarea.optional',
                          })}
                    </BadgeCopy>
                  </Badge>
                </Flex>
              </InputGroup>
            </InputRoot>
          </Flex>
        )}
        {register && itemId && (
          <>
            <input
              type="hidden"
              {...register(`items.${itemId}.reason.isCommentRequired`)}
            />
            <input
              type="hidden"
              {...register(`items.${itemId}.reason.isAttestationRequired`)}
            />
          </>
        )}
        {photoConfig?.enabled && (
          <Flex direction="column" gap="g-16">
            <h3 id="photosTitle" className={s.fieldTitle}>
              <Text
                langKey={[
                  `Global.reasons.photos.header.${reasonId}`,
                  'Global.reasons.photos.header',
                ]}
              />
            </h3>
            <PhotoUpload
              metadataId={photoConfig.id}
              photos={photos}
              max={photoConfig.max ?? 2}
              onChange={onPhotoUpload}
              onRemove={onPhotoRemove}
              onError={onPhotoReset}
            />
          </Flex>
        )}
        {attestationConfig?.enabled && (
          <Flex direction="column" gap="g-16">
            <AttestationCheckbox
              id={`${itemId}-attestation`}
              reasonId={reasonId}
              required={Boolean(attestationConfig?.required)}
              checked={Boolean(attestationChecked)}
              onChange={onAttestationChange}
            />
          </Flex>
        )}
      </Flex>
    </>
  );
};
